import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import {
  applySizeGuideUnit,
  bindSizeGuideTableScroll,
  prepareSizeGuideTables,
} from '@theme/bt-size-guide-tables';

/**
 * PDP size guide drawer — mobile bottom sheet, desktop right-slide, cm/in toggle.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} [unitSwitch]
 * @property {HTMLElement} [body]
 *
 * @extends {DialogComponent}
 */
class BtSizeGuideDrawer extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {import('./bt-scroll-carousel').OverflowCarouselBinding[]} */
  #tableScrollBindings = [];

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    this.#prepareTables();
    this.#bindTableScroll();
    customElements.whenDefined('rte-formatter').then(() => {
      if (!this.isConnected) return;
      requestAnimationFrame(() => this.#bindTableScroll());
    });
    this.addEventListener(DialogOpenEvent.eventName, this.#onDialogOpen, { signal });
    this.refs.unitSwitch?.addEventListener('click', this.#onToggle, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#abortController = null;
    this.#disconnectTableScroll();
  }

  #onDialogOpen = () => {
    requestAnimationFrame(() => {
      for (const binding of this.#tableScrollBindings) binding.update();
    });
  };

  #bindTableScroll() {
    const body = this.refs.body;
    if (!(body instanceof HTMLElement)) return;

    this.#tableScrollBindings.push(
      ...bindSizeGuideTableScroll(body, {
        tableClass: 'bt-size-guide__table',
        trackClass: 'bt-size-guide__track',
        scrollClass: 'bt-size-guide__scroll',
        fillClass: 'bt-size-guide__scroll-fill',
      })
    );
  }

  #disconnectTableScroll() {
    for (const binding of this.#tableScrollBindings) binding.disconnect();
    this.#tableScrollBindings = [];
  }

  #prepareTables() {
    const body = this.refs.body;
    if (!(body instanceof HTMLElement)) return;

    const hasUnits = prepareSizeGuideTables(body);
    const toggle = this.querySelector('[data-bt-size-guide-toggle]');
    if (toggle instanceof HTMLElement) toggle.hidden = !hasUnits;
    this.dataset.unit = 'cm';
  }

  #onToggle = () => {
    this.#applyUnit(this.dataset.unit === 'in' ? 'cm' : 'in');
  };

  /**
   * @param {'cm' | 'in'} unit
   */
  #applyUnit(unit) {
    this.dataset.unit = unit;
    const sw = this.refs.unitSwitch;
    if (sw instanceof HTMLButtonElement) {
      sw.setAttribute('aria-checked', unit === 'in' ? 'true' : 'false');
    }

    const body = this.refs.body;
    if (body instanceof HTMLElement) applySizeGuideUnit(body, unit);
  }
}

if (!customElements.get('bt-size-guide-drawer')) {
  customElements.define('bt-size-guide-drawer', BtSizeGuideDrawer);
}
